import PhotoSwipeLightbox from 'photoswipe/lightbox';
import type PhotoSwipe from 'photoswipe';
import { localeText } from '../locale';
import { shouldCloseOnDownFling } from './gallery-gestures';

interface GalleryScopeMeta {
  folio: string;
  title: string;
}

interface GallerySlideData {
  key: string;
  src: string;
  srcset?: string | undefined;
  width: number;
  height: number;
  alt: string;
  caption: string;
  folio: string;
  title: string;
  narrativeTarget?: string | undefined;
}

interface GalleryRegistry {
  gallery: HTMLElement;
  slides: GallerySlideData[];
  openers: HTMLElement[];
  indexByKey: Map<string, number>;
  canonicalOpenerByKey: Map<string, HTMLElement>;
}

const openerSelector = '[data-ids-gallery-open][data-ids-photo-key]';

function text(node: Element | null): string {
  return node?.textContent?.trim() ?? '';
}

function chapterMeta(scope: HTMLElement): GalleryScopeMeta {
  const opening = scope.querySelector<HTMLElement>('.is-chapter-opening');
  const eyebrow = text(opening?.querySelector('.ep-eyebrow') ?? null);
  return {
    folio: eyebrow.match(/\d{2}/)?.[0] ?? scope.id.replace('chapter-', ''),
    title: text(opening?.querySelector('h2, h3') ?? null)
      || localeText('Документальная биография', 'Даректүү өмүр баян'),
  };
}

function annotateScope(scope: HTMLElement, meta: GalleryScopeMeta): void {
  for (const opener of scope.querySelectorAll<HTMLElement>(openerSelector)) {
    opener.dataset.idsGalleryFolio = meta.folio;
    opener.dataset.idsGalleryTitle = meta.title;
  }
}

function orderedOpeners(gallery: HTMLElement): HTMLElement[] {
  const narrativeScopes = [
    gallery.querySelector<HTMLElement>('.ep-intro'),
    ...gallery.querySelectorAll<HTMLElement>('[data-ep-chapter]'),
    gallery.querySelector<HTMLElement>('.ep-end'),
  ].filter((scope): scope is HTMLElement => scope !== null);
  const ordered: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();
  const append = (opener: HTMLElement): void => {
    if (seen.has(opener)) return;
    seen.add(opener);
    ordered.push(opener);
  };

  for (const scope of narrativeScopes) {
    for (const opener of scope.querySelectorAll<HTMLElement>(openerSelector)) append(opener);
  }
  for (const opener of gallery.querySelectorAll<HTMLElement>(openerSelector)) append(opener);
  return ordered;
}

function narrativeTarget(opener: HTMLElement): string | undefined {
  return opener.dataset.idsNarrativeTarget
    || opener.closest<HTMLElement>('.ep-story-gallery[id], .ep-intro[id], .ep-end[id]')?.id
    || opener.closest<HTMLElement>('[data-ep-chapter][id]')?.id
    || undefined;
}

function slideFromOpener(opener: HTMLElement): GallerySlideData | null {
  const key = opener.dataset.idsPhotoKey;
  const src = opener.dataset.pswpSrc
    || (opener instanceof HTMLAnchorElement ? opener.href : '');
  const width = Number(opener.dataset.pswpWidth);
  const height = Number(opener.dataset.pswpHeight);
  if (!key || !src || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  const image = opener.querySelector<HTMLImageElement>('img')
    ?? opener.closest<HTMLElement>('.ep-story-gallery__item')?.querySelector<HTMLImageElement>('img');
  return {
    key,
    src,
    srcset: opener.dataset.pswpSrcset || undefined,
    width,
    height,
    alt: image?.alt.trim() || opener.dataset.idsCaption?.trim() || '',
    caption: opener.dataset.idsCaption?.trim() || image?.alt.trim() || '',
    folio: opener.dataset.idsGalleryFolio || localeText('Архив', 'Архив'),
    title: opener.dataset.idsGalleryTitle
      || text(opener.closest('.ep-story-gallery')?.querySelector('h2, h3') ?? null)
      || localeText('Документальная биография', 'Даректүү өмүр баян'),
    narrativeTarget: narrativeTarget(opener),
  };
}

function buildRegistry(root: ParentNode): GalleryRegistry | null {
  const gallery = root instanceof HTMLElement
    ? root
    : root.querySelector<HTMLElement>('#assembled-biography');
  if (!gallery) return null;

  const intro = gallery.querySelector<HTMLElement>('.ep-intro');
  if (intro) {
    intro.id ||= 'biography-intro';
    annotateScope(intro, {
      folio: localeText('Пролог', 'Баш сөз'),
      title: text(intro.querySelector('h1')) || 'Аманбек Карыпкулов',
    });
  }
  for (const chapter of gallery.querySelectorAll<HTMLElement>('[data-ep-chapter]')) {
    annotateScope(chapter, chapterMeta(chapter));
  }
  const ending = gallery.querySelector<HTMLElement>('.ep-end');
  if (ending) {
    ending.id ||= 'biography-ending';
    annotateScope(ending, {
      folio: localeText('Эпилог', 'Соңку сөз'),
      title: text(ending.querySelector('h2')) || localeText('Семейный архив', 'Үй-бүлөлүк архив'),
    });
  }

  const openers = orderedOpeners(gallery);
  for (const opener of openers) {
    if (opener.dataset.idsGalleryFolio) continue;
    opener.dataset.idsGalleryFolio = localeText('Архив', 'Архив');
    opener.dataset.idsGalleryTitle = text(opener.closest('.ep-story-gallery')?.querySelector('h2, h3') ?? null)
      || opener.dataset.idsCaption
      || localeText('Документальная биография', 'Даректүү өмүр баян');
  }

  const slides: GallerySlideData[] = [];
  const indexByKey = new Map<string, number>();
  const canonicalOpenerByKey = new Map<string, HTMLElement>();
  for (const opener of openers) {
    const key = opener.dataset.idsPhotoKey;
    if (!key || indexByKey.has(key)) continue;
    const slide = slideFromOpener(opener);
    if (!slide) continue;
    indexByKey.set(key, slides.length);
    canonicalOpenerByKey.set(key, opener);
    slides.push(slide);
  }
  if (!slides.length) return null;

  gallery.classList.add('ids-gallery');
  gallery.dataset.idsGallery = '';
  return { gallery, slides, openers, indexByKey, canonicalOpenerByKey };
}

function currentSlide(pswp: PhotoSwipe): GallerySlideData | undefined {
  return pswp.currSlide?.data as GallerySlideData | undefined;
}

function updateFolio(element: HTMLElement, pswp: PhotoSwipe): void {
  const slide = currentSlide(pswp);
  const current = pswp.currIndex + 1;
  const total = pswp.getNumItems();
  const meta = document.createElement('span');
  meta.className = 'ids-gallery__folio-meta';
  meta.textContent = `${slide?.folio || localeText('Архив', 'Архив')} · ${String(current).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
  const heading = document.createElement('strong');
  heading.textContent = slide?.title || 'Аманбек Карыпкулов';
  element.replaceChildren(meta, heading);
}

function updateCaption(element: HTMLElement, pswp: PhotoSwipe): void {
  const caption = currentSlide(pswp)?.caption ?? '';
  element.textContent = caption;
  element.hidden = !caption;
}

function thumbnailElement(opener: HTMLElement | undefined): HTMLElement | null {
  if (!opener) return null;
  return opener.querySelector<HTMLElement>('img')
    ?? opener.closest<HTMLElement>('.ep-story-gallery__item')?.querySelector<HTMLElement>('img')
    ?? null;
}

function navigateToNarrative(targetId: string): void {
  const target = document.getElementById(targetId);
  if (!target) return;
  const hash = `#${targetId}`;
  if (window.location.hash === hash) window.history.replaceState(null, '', hash);
  else window.history.pushState(null, '', hash);
  target.scrollIntoView({ block: 'start', behavior: 'auto' });
  const focusTarget = target.querySelector<HTMLElement>('h1, h2, h3') ?? target;
  focusTarget.tabIndex = -1;
  focusTarget.focus({ preventScroll: true });
}

export function mountIdsGallery(root: ParentNode = document): PhotoSwipeLightbox | null {
  const registry = buildRegistry(root);
  if (!registry) return null;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const phoneViewport = window.matchMedia('(max-width: 700px)');
  let activeOpener: HTMLElement | null = null;
  let activePhotoKey = '';
  let pendingNarrativeTarget = '';
  const activeTouchPointers = new Set<number>();
  let downFlingStart: {
    pointerId: number;
    x: number;
    y: number;
    time: number;
    hadMultiplePointers: boolean;
  } | null = null;
  let downFlingAbort: AbortController | null = null;

  const lightbox = new PhotoSwipeLightbox({
    dataSource: registry.slides,
    pswpModule: () => import('photoswipe'),
    mainClass: 'ids-gallery--open',
    bgOpacity: 1,
    spacing: 0.08,
    loop: false,
    allowPanToNext: true,
    pinchToClose: false,
    closeOnVerticalDrag: true,
    wheelToZoom: true,
    escKey: true,
    arrowKeys: true,
    trapFocus: true,
    returnFocus: false,
    clickToCloseNonZoomable: false,
    imageClickAction: 'zoom',
    bgClickAction: false,
    tapAction: 'toggle-controls',
    doubleTapAction: 'zoom',
    preload: [1, 2],
    initialZoomLevel: 'fit',
    secondaryZoomLevel: 2,
    maxZoomLevel: 4,
    closeSVG: '<svg aria-hidden="true" class="pswp__icn ids-gallery__close-icon" viewBox="0 0 32 32" width="32" height="32"><path d="M10 10 22 22M22 10 10 22"/></svg>',
    closeTitle: localeText('Закрыть', 'Жабуу'),
    arrowPrevTitle: localeText('Предыдущая фотография', 'Мурунку сүрөт'),
    arrowNextTitle: localeText('Следующая фотография', 'Кийинки сүрөт'),
    errorMsg: localeText('Не удалось загрузить фотографию', 'Сүрөт жүктөлгөн жок'),
    showAnimationDuration: reducedMotion ? 0 : 280,
    hideAnimationDuration: reducedMotion ? 0 : 240,
    zoomAnimationDuration: reducedMotion ? 0 : 280,
    easing: 'cubic-bezier(.22, .72, .2, 1)',
    paddingFn: ({ x, y }) => {
      if (phoneViewport.matches) return { top: 0, bottom: 0, left: 0, right: 0 };
      const horizontal = Math.max(72, x * 0.06);
      return {
        top: Math.max(64, y * 0.08),
        bottom: Math.max(150, y * 0.18),
        left: horizontal,
        right: horizontal,
      };
    },
  });
  const zoomCurrentPhoneSlideToFill = (): void => {
    if (!phoneViewport.matches) return;
    const slide = lightbox.pswp?.currSlide;
    if (!slide || slide.zoomLevels.fill <= slide.zoomLevels.initial) return;
    slide.zoomTo(slide.zoomLevels.fill, undefined, 0);
  };

  lightbox.on('change', zoomCurrentPhoneSlideToFill);

  lightbox.addFilter('thumbEl', (thumbEl, data) => {
    const slide = data as GallerySlideData;
    return thumbnailElement(
      slide.key === activePhotoKey && activeOpener
        ? activeOpener
        : registry.canonicalOpenerByKey.get(slide.key),
    ) ?? thumbEl ?? registry.gallery;
  });
  lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
    const data = slide.data as GallerySlideData;
    const opener = data.key === activePhotoKey && activeOpener
      ? activeOpener
      : registry.canonicalOpenerByKey.get(data.key);
    return thumbnailElement(opener)?.getAttribute('src') || placeholderSrc;
  });

  const onDownFlingPointerDown = (originalEvent: PointerEvent): void => {
    if (originalEvent.pointerType !== 'touch') return;
    activeTouchPointers.add(originalEvent.pointerId);
    if (activeTouchPointers.size === 1) {
      downFlingStart = {
        pointerId: originalEvent.pointerId,
        x: originalEvent.clientX,
        y: originalEvent.clientY,
        time: originalEvent.timeStamp,
        hadMultiplePointers: false,
      };
    } else if (downFlingStart) {
      downFlingStart.hadMultiplePointers = true;
    }
  };

  const onDownFlingPointerUp = (originalEvent: PointerEvent): void => {
    if (originalEvent.pointerType !== 'touch') return;
    const start = downFlingStart;
    const hadMultiplePointers = Boolean(start?.hadMultiplePointers || activeTouchPointers.size > 1);
    activeTouchPointers.delete(originalEvent.pointerId);
    if (
      originalEvent.type === 'pointercancel'
      || !start
      || start.pointerId !== originalEvent.pointerId
      || activeTouchPointers.size > 0
    ) {
      if (activeTouchPointers.size === 0) downFlingStart = null;
      return;
    }

    downFlingStart = null;
    const gesture = {
      deltaX: originalEvent.clientX - start.x,
      deltaY: originalEvent.clientY - start.y,
      durationMs: originalEvent.timeStamp - start.time,
      hadMultiplePointers,
    };
    const shouldClose = shouldCloseOnDownFling(gesture);
    if (!shouldClose) return;
    const pswp = lightbox.pswp;
    if (!pswp) return;
    window.setTimeout(() => pswp.close(), 80);
  };

  lightbox.on('afterInit', () => {
    const dialog = lightbox.pswp?.element;
    if (!dialog) return;
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', localeText('Полноэкранный просмотр фотографий', 'Сүрөттөрдү толук экранда көрүү'));
    dialog.setAttribute('aria-describedby', 'ids-gallery-caption');
    downFlingAbort?.abort();
    downFlingAbort = new AbortController();
    const { signal } = downFlingAbort;
    dialog.addEventListener('pointerdown', onDownFlingPointerDown, { signal });
    window.addEventListener('pointerup', onDownFlingPointerUp, { signal });
    window.addEventListener('pointercancel', onDownFlingPointerUp, { signal });
    zoomCurrentPhoneSlideToFill();
  });

  lightbox.on('keydown', ({ originalEvent }) => {
    if (originalEvent.key !== 'Tab') return;
    const dialog = lightbox.pswp?.element;
    if (!dialog) return;
    const controls = [...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    )].filter((control) => control.offsetParent !== null);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!first || !last) return;
    if (originalEvent.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
      originalEvent.preventDefault();
      last.focus();
    } else if (!originalEvent.shiftKey && document.activeElement === last) {
      originalEvent.preventDefault();
      first.focus();
    }
  });

  lightbox.on('uiRegister', () => {
    const pswp = lightbox.pswp;
    const ui = pswp?.ui;
    if (!pswp || !ui) return;

    ui.registerElement({
      name: 'ids-folio',
      className: 'ids-gallery__folio',
      appendTo: 'root',
      onInit: (element, instance) => {
        element.setAttribute('aria-hidden', 'true');
        const update = (): void => updateFolio(element, instance);
        instance.on('change', update);
        update();
      },
    });
    ui.registerElement({
      name: 'ids-caption',
      className: 'ids-gallery__caption',
      appendTo: 'root',
      onInit: (element, instance) => {
        element.id = 'ids-gallery-caption';
        const update = (): void => updateCaption(element, instance);
        instance.on('change', update);
        update();
      },
    });
    ui.registerElement({
      name: 'ids-dismiss-hint',
      className: 'ids-gallery__dismiss-hint',
      appendTo: 'root',
      html: localeText('Смахните вниз, чтобы закрыть', 'Жабуу үчүн ылдый сүрүңүз'),
      onInit: (element) => element.setAttribute('aria-hidden', 'true'),
    });
    ui.registerElement({
      name: 'ids-return',
      className: 'pswp__button--return',
      appendTo: 'root',
      isButton: true,
      title: localeText('Вернуться к фотографии', 'Сүрөткө кайтуу'),
      html: localeText('← Назад', '← Артка'),
      onClick: (_event, _element, instance) => instance.close(),
    });
    ui.registerElement({
      name: 'ids-story-link',
      className: 'ids-gallery__story-link',
      appendTo: 'root',
      isButton: true,
      title: localeText('Перейти к месту фотографии в истории', 'Сүрөттүн баяндагы жерине өтүү'),
      html: localeText('К истории ↓', 'Баянга ↓'),
      onInit: (element, instance) => {
        const update = (): void => {
          const target = currentSlide(instance)?.narrativeTarget;
          element.hidden = !target || !document.getElementById(target);
        };
        instance.on('change', update);
        update();
      },
      onClick: (_event, _element, instance) => {
        const target = currentSlide(instance)?.narrativeTarget;
        if (!target || !document.getElementById(target)) return;
        pendingNarrativeTarget = target;
        instance.close();
      },
    });
  });

  lightbox.on('destroy', () => {
    downFlingAbort?.abort();
    downFlingAbort = null;
    activeTouchPointers.clear();
    downFlingStart = null;
    const target = pendingNarrativeTarget;
    const opener = activeOpener;
    pendingNarrativeTarget = '';
    activeOpener = null;
    window.requestAnimationFrame(() => {
      if (target) {
        navigateToNarrative(target);
        return;
      }
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
  });

  lightbox.init();
  for (const opener of registry.openers) {
    opener.addEventListener('click', (event) => {
      if (
        event instanceof MouseEvent
        && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) return;
      const key = opener.dataset.idsPhotoKey;
      const index = key ? registry.indexByKey.get(key) : undefined;
      if (index === undefined) return;
      event.preventDefault();
      activeOpener = opener;
      activePhotoKey = key ?? '';
      const point = event instanceof MouseEvent ? { x: event.clientX, y: event.clientY } : undefined;
      lightbox.loadAndOpen(index, registry.slides, point);
    });
  }
  return lightbox;
}
